/**
 * Skeleton-level fallback translations — generic, tenant-agnostic platform
 * copy that has no business being duplicated in every tenant package.
 * `dashboardSections` in particular is Studio-driven-dashboard-engine
 * boilerplate ("Your policies", "COVER STARTS", "No claims yet", ...): the
 * wording is identical regardless of tenant, so a freshly scaffolded tenant
 * package that ships `translations: {}` (the scaffold's own documented
 * starting point — see each tenant package's src/data/index.ts comment)
 * still renders real text instead of next-intl's raw-key fallback.
 *
 * `_app.tsx` deep-merges this UNDER whatever the active tenant package
 * provides (mergeDeep(DEFAULT_TRANSLATIONS, pageProps.translations)) — a
 * tenant can still override any individual key (or whole namespace) with
 * its own wording; anything it doesn't provide falls through to here.
 *
 * Keep this to genuinely generic copy only. Anything brand-specific (nav
 * link labels, landing copy, legal text) belongs in the tenant package, not
 * here.
 *
 * Flat shape (not locale-keyed): this merges against `pageProps.translations`
 * AFTER `getPageContent()` has already resolved it down to the current
 * locale (`{...translations.common, ...translations[locale]}`), so there's
 * no `en`/`ar` wrapper to match here. English-only for now — add a locale
 * switch in the merge call in `_app.tsx` if/when a non-English default set
 * is needed.
 */
export const DEFAULT_TRANSLATIONS = {
	nav: {
		login: 'Login',
		dashboard: 'Dashboard',
		logout: 'Logout',
		help: 'Help',
		language: 'Language',
		poweredBy: 'Powered by',
		// Screen-reader label for the mobile sidebar toggle. Was a hardcoded
		// English string in Navbar.tsx, so an Arabic reader got an English
		// announcement on the one control that has no visible text.
		toggleSidebar: 'Toggle sidebar',
	},
	// Separate from `nav.poweredBy` above — components/Layout.tsx's
	// authenticated Sidebar footer reads `dashboard.poweredBy` specifically
	// (a legacy-CMS-era namespace name that stuck), not `nav.poweredBy`.
	// Same literal wording, different key, both needed.
	dashboard: {
		poweredBy: 'Powered by',
	},
	pageBuilder: {
		from: 'From',
		noCoverages: 'No coverages available.',
		getQuote: 'Get a quote',
	},
	errors: {
		notFoundTitle: 'Page Not Found',
		returnHome: 'Return to Home',
	},
	form: {
		login: 'Login',
		back: 'Back',
		next: 'Next',
		pay: 'Pay',
		applicationBreadcrumbHome: 'Home',
		applicationBreadcrumbForm: 'Insurance Application Form',
		applicationStartTitle: 'Start Your Insurance Application',
		applicationStartDescription:
			'Begin your {tenant} community insurance application in minutes. Complete your onboarding securely and customise your cover to suit your needs.',
		providerPickerTitle: 'Choose your provider',
		providerPickerPlaceholder: 'Select a provider',
		journeyResumeTitle:
			'Do you want to continue your registration from where you left off?',
		journeyContinue: 'Continue',
		journeyStartOver: 'Start over again',
		annualPremium: '{price, number, currency} per year',
		perYear: 'per year',
		perMonth: 'per month',
		payLaterTotal: 'total, split into instalments',
		oneTimeTotal: 'one-time payment',
		// A native <input type='file'> labels itself in the BROWSER's language,
		// never the page's, so PhotoOfIdPassthrough.tsx hides the control and
		// draws its own chooser from these two keys.
		chooseFile: 'Choose file',
		noFileChosen: 'No file chosen',
		// Payment-step chrome. The summary card's own headings are platform
		// copy, not a tenant's wording, so they live here and a tenant only
		// ships the locales it needs (see Payment.tsx).
		applicationSummary: 'Application Summary',
		applicationDetails: 'Application Details',
		additionalDetails: 'Additional Details',
		selectPaymentMethod: 'Select Payment Method',
		// Offer comparison (quote_comparison sections) and in-place
		// integration buttons. Generic platform copy, so it belongs here
		// rather than being inlined as an isArabic ternary in the component.
		offerCount: '{count} offers',
		sortOffers: 'Sort offers',
		offersScroller: 'Offers — scroll sideways to see them all',
		previousOffers: 'Previous offers',
		nextOffers: 'Next offers',
		sortRecommended: 'Recommended',
		sortPriceLowHigh: 'Price: low to high',
		sortPriceHighLow: 'Price: high to low',
		filterCoverLevel: 'Filter by cover level',
		allCoverLevels: 'All cover levels',
		chooseOffer: 'Choose this offer',
		offerSelected: 'Selected',
		policyDetails: 'Policy details',
		hidePolicyDetails: 'Hide policy details',
		discountsApplied: '{count} discounts applied',
		offersLoadError:
			'We could not load the insurance offers. Please try again.',
		chooseOfferBeforeContinuing: 'Choose an insurance offer before continuing.',
		integrationActionFailed:
			'That lookup could not be completed. Please try again.',
		integrationActionEmpty:
			'The service returned no details for these answers.',
		integrationActionNeedsFields: 'Fill in {fields} first.',
		includesTax: 'Includes 12% Insurance Premium Tax (IPT)',
		quoteSaved:
			'This quote has been saved and emailed to you. It is valid for 90 days.',
		quoteReferenceNumber: 'Quote Reference Number',
		otpVerified: 'Phone number verified',
		otpEnterCode:
			'Please enter the verification code we sent to {mobileNumber}',
		otpNotReceived: "Didn't receive the code?",
		otpResend: 'Resend code',
		otpInvalid:
			'That code was invalid. Please check your number and try again.',
		otpVerify: 'Verify',
		otpSuccess: 'Mobile number verified successfully.',
		otpEnterMobile: 'Enter a mobile number before continuing.',
		otpMisconfigured: 'This verification step is not configured correctly.',
		otpSendFailed: 'The verification code could not be sent.',
		otpSending: 'Sending a verification code…',
		otpCodePrompt: 'Enter the {count}-digit code sent to {mobile}.',
		otpMobileFallback: 'your mobile number',
		otpCodeLabel: 'Verification code',
		otpVerifying: 'Verifying…',
		otpResendIn: 'Resend code in {seconds}s',
		otpSend: 'Send code',
		yearMismatch:
			"This date doesn't match the year of birth entered earlier. This may affect the price from your quick quote.",
		countMismatch:
			'There are a different number of family members from your quick quote. This may affect the price.',
		yearOfBirthRange: 'Enter a year of birth between {min} and {max}.',
		yearOfBirthRangeFor:
			'Enter a year of birth between {min} and {max} for {members}.',
		yearOfBirthIncomplete:
			'Enter the full four-digit year, between {min} and {max}.',
		yearOfBirthAgeLimit:
			'Cover is available up to age {maxAge}, so the earliest year of birth we can accept is {min}.',
		yearOfBirthAgeLimitFor:
			'Cover is available up to age {maxAge}, so {members} must be born in {min} or later.',
		yearOfBirthDependantAge:
			'{members} must be aged {maxAge} or under — enter a year of birth of {min} or later.',
		yearOfBirthAge: 'Age {age}',
		paymentFormLoading: 'Loading payment form…',
		paymentProcessing: 'Processing…',
		payNow: 'Pay now',
		'Setup Password': 'Setup Password',
		'Payment successful!': 'Payment successful!',
		'Payment processing':
			"Payment processing. We'll update you when payment is received.",
		'Payment failed': 'Payment failed. Please try another payment method.',
		'Payment state unknown': 'Something went wrong.',
		payStatusMessage:
			'A copy of your policy will be sent to you via email, along with a link to your personal insurance dashboard.',
		paymentReceived: 'Payment of {amount, number, currency} received.',
		installmentCreated:
			'Monthly installment of {amount, number, currency} created with subscription ID {subscriptionId}',
	},
	payment: {
		failure: {
			title: 'Payment Failed',
			unknown: 'An unknown error occurred',
			reference: 'Reference: {reference}',
			tryAgain: 'Try Again',
			goHome: 'Go to Home',
		},
		errors: {
			generic: 'Payment failed. Please try another payment method.',
			start: 'We could not start your payment.',
			methods: 'Could not load payment methods.',
			completionTitle: "We couldn't finish your purchase",
		},
		checkout: {
			title: 'Payment',
			completionRetry: 'Try again',
			confirming: 'Confirming your payment…',
			doNotClose: "Please don't close this page.",
			total: 'Total to pay',
			loadingMethods: 'Loading payment methods…',
			unavailableTitle: "Online payment isn't available yet",
			unavailableDescription:
				'No payment method has been activated for this product. Please contact us to complete your purchase.',
			amountMissingTitle: "We couldn't confirm the amount to pay",
			amountMissingDescription:
				"Please refresh the page and try again. If it keeps happening, contact us rather than paying — we don't want to charge you the wrong amount.",
			completeDetails: 'Please complete the details above to continue.',
			preparing: 'Preparing payment…',
			payNow: 'Pay now',
		},
	},
	misc: {
		'Back to homepage': 'Back to homepage',
		'ineligible-head': "Sorry, we can't offer you cover currently",
		'ineligible-description':
			"At times, we might be unable to provide you the cover you need. It could be because we're not set up to cover this just yet, or maybe your circumstances don't quite match our current offerings.",
	},
	dashboardSections: {
		genericError: 'Could not load this section right now.',
		policySummary: {
			title: 'Your policies',
			empty: 'You have no policies yet.',
			activeCovers:
				'{count, plural, one {# active cover} other {# active covers}}',
			coverStarts: 'COVER STARTS',
			coverEnds: 'COVER ENDS',
			premium: 'PREMIUM',
		},
		documents: {
			title: 'Your documents',
			empty: 'No documents available yet.',
			columnDocument: 'Document',
			columnPolicy: 'Policy',
			columnCreated: 'Created',
		},
		paymentsHistory: {
			title: 'Payment history',
			empty: 'No payments recorded yet.',
			columnPolicy: 'Policy',
			columnAmount: 'Amount',
			columnMethod: 'Method',
			columnDate: 'Date',
		},
		renewalStatus: {
			title: 'Renewal status',
			empty: 'No upcoming renewals to show.',
			coverEndedAgo:
				'Cover ended {days, plural, one {# day} other {# days}} ago',
			coverEndsIn: 'Cover ends in {days, plural, one {# day} other {# days}}',
			lapsed: 'Lapsed',
			renewSoon: 'Renew soon',
		},
		coveredMembers: {
			title: 'Covered members',
			empty: 'No members on file yet.',
			columnName: 'Name',
			columnRelationship: 'Relationship',
			columnPolicy: 'Policy',
			principal: 'Principal',
		},
		claimsList: {
			title: 'Claims',
			empty: 'No claims yet.',
			columnReference: 'Reference',
			columnPolicy: 'Policy',
			columnStatus: 'Status',
			columnIncidentDate: 'Incident date',
			columnDocuments: 'Documents',
		},
		instalmentSchedule: {
			title: 'Instalment schedule',
			empty: 'No instalment plans to show.',
			columnPolicy: 'Policy',
			columnAmount: 'Amount',
			columnFrequency: 'Frequency',
			columnProgress: 'Progress',
			columnNextDue: 'Next due',
			columnStatus: 'Status',
			columnPaymentMethod: 'Payment method',
			updatePaymentMethod: 'Update card',
			updateTitle: 'Update payment card',
			updateDescription:
				'Enter the card you want us to use for future recurring payments. Your card details are sent securely to the payment provider and are not stored by us.',
			updateCancel: 'Cancel',
			updateSave: 'Save card',
			updateSuccess: 'Payment card updated',
			updatedCard: 'Future payments will use the card ending in {last4}.',
			updateError: 'Could not update the payment card.',
			updateConfigurationError:
				'The payment provider is not configured for secure card updates.',
			// Card fields for gateways that collect the replacement card with
			// their own browser SDK rather than hosted iframe fields.
			cardName: 'Name on card',
			cardNumber: 'Card number',
			cardMonth: 'Expiry month',
			cardYear: 'Expiry year',
			cardCvc: 'Security code',
		},
		myQuotes: {
			title: 'My quotes',
			empty: 'No open quotes to show.',
			columnProduct: 'Product',
			columnReference: 'Reference',
			columnPremium: 'Premium',
			columnStatus: 'Status',
			columnValidUntil: 'Valid until',
		},
		profileDetails: {
			title: 'Your details',
			email: 'EMAIL',
			phone: 'PHONE',
			phoneUnknown: 'Not on file',
			memberSince: 'MEMBER SINCE',
		},
		referrals: {
			title: 'Referrals',
			inviteMessage:
				"Share your link — when a friend buys a policy, it'll show up here.",
			signupCount: '{count, plural, one {# signup} other {# signups}}',
			copyLink: 'Copy link',
			copied: 'Copied!',
			empty: 'No signups yet — share your link to get started.',
		},
		complaints: {
			title: 'Complaints',
			empty: 'No complaints on file.',
			columnReference: 'Reference',
			columnCategory: 'Category',
			columnStatus: 'Status',
			columnCreated: 'Logged',
			lodgeButton: 'Log a complaint',
			formCategory: 'Category',
			formDescription: 'Tell us what happened',
			formSubmit: 'Submit complaint',
			formCancel: 'Cancel',
			submitError: 'Could not submit your complaint. Please try again.',
			submitted: 'Your complaint has been logged.',
			resolutionNote: 'Resolution',
		},
		rewards: {
			title: 'Your Rewards',
			empty: 'No rewards available yet.',
			unlockReward: 'Unlock reward',
			claimReward: 'Claim reward',
			unlockHint:
				'Refer {count, plural, one {# friend} other {# friends}} to unlock',
			claimError: 'Could not claim this reward. Please try again.',
			claimStatus: {
				pending: 'Claim pending',
				approved: 'Claim approved',
				fulfilled: 'Reward fulfilled',
				rejected: 'Claim rejected',
				cancelled: 'Claim cancelled',
			},
		},
	},
	// Sales-dashboard bulk-policy pages (My policy, Bulk policies, Profile).
	// Data text — company names, column headers, value labels, state of a
	// purchase — arrives localised from Studio; these are only the chrome.
	salesDashboard: {
		// Sidebar labels, keyed by portal page id (lib/tenantAdminPortalNav.ts).
		nav: {
			home: 'Overview',
			sales: 'Sales',
			policies: 'Policies',
			all_claims: 'All Claims',
			kyc_ineligibility: 'KYC Ineligibility',
			ineligibility: 'Ineligibility',
			demographics: 'Demographics',
			my_policies: 'My policy',
			bulk_policies: 'Bulk policies',
			new_form_quote: 'New Form Quote',
			profile: 'Profile',
		},
		states: {
			issued: 'Issued',
			preparing_members: 'Preparing members',
			issuing: 'Being issued',
			attention: 'Needs attention',
			cancelled: 'Cancelled',
			preparingMembersMessage: 'Preparing your member list…',
			issuingMessage: 'Your policy is being issued…',
			attentionMessage:
				"We're finalising your policy. Our team has been notified.",
			submittedMembers:
				'{count, plural, one {# member submitted} other {# members submitted}}',
		},
		// Staff Home card (section `bulk_policies_summary`).
		bulkSummary: {
			title: 'Bulk policies',
			subtitle:
				'Group purchases for the selected month, and those needing attention.',
			purchases: 'Purchases',
			members: 'Members',
			premium: 'Premium',
			attentionTitle: 'Needing attention',
			stuckFor: 'Stuck for {age}',
			ageMinutes: '{count} min',
			ageHours: '{count} h',
			ageDays: '{count} d',
			viewMembers: 'View members',
			more: '+ {count} more',
			empty: 'No bulk purchases yet.',
			loadError: 'Could not load the bulk policies summary.',
		},
		bulkPolicies: {
			title: 'Bulk policies',
			searchPlaceholder: 'Search company, registration, policy or reference',
			allStates: 'All states',
			empty: 'No bulk policies yet.',
			loadError: 'Could not load bulk policies. Please try again.',
			columnCompany: 'Company',
			columnPolicy: 'Policy',
			columnCover: 'Cover',
			columnMembers: 'Members',
			columnPremium: 'Premium',
			columnState: 'State',
			beingIssued: 'Being issued',
			viewMembers: 'View members',
			policyDetails: 'Policy details',
			back: 'Back to bulk policies',
			notFound: 'This policy could not be found.',
		},
		members: {
			search: 'Search',
			export: 'Export',
			exportDisabled: 'Export is not available.',
			exportLimit: 'Export limit reached. Please try again later.',
			exportError: 'Could not export the member list.',
			loadError: 'Could not load members. Please try again.',
			pageOf: 'Page {page} of {pages}',
			previous: 'Previous',
			next: 'Next',
			total: '{count, plural, one {# member} other {# members}}',
		},
		// Member certificates. `{item}` is what one member is called on the
		// purchase's form ("vehicle", "employee"), or `member` below.
		certificates: {
			member: 'member',
			column: 'Certificate',
			download: 'Download',
			downloadLabel: 'Download the certificate of {name}',
			downloadAll: 'Download all (ZIP)',
			progress: '{item} certificates: {generated} of {total} ready',
			generating: 'Generating…',
			failed: 'Failed',
			regenerate: 'Regenerate certificate',
			regenerated: 'Certificate regenerated.',
			stillFailing:
				'The certificate could still not be created. Our team has been notified.',
			regenerateError: 'Could not regenerate the certificate. Please try again.',
			unavailable: 'Certificates are no longer available for this policy.',
			limit: 'Limit reached. Please try again later.',
			notReady: 'No certificates are ready yet.',
			downloadError: 'Could not download. Please try again.',
			failedAlert:
				'{count, plural, one {# {item} certificate could not be created.} other {# {item} certificates could not be created.}} The policy itself is issued and valid. Use Regenerate on each flagged {item}.',
		},
		myPolicy: {
			title: 'My policy',
			empty: 'You have no policies yet.',
			loadError: 'Could not load your policy. Please try again.',
			notFound: 'This policy could not be found.',
			back: 'Back to my policies',
			viewDetails: 'View details',
			policyNumber: 'Policy number',
			coverStarts: 'Cover starts',
			coverEnds: 'Cover ends',
			issuedOn: 'Issued on',
			reference: 'Reference',
			insurerAndPlan: 'Insurer and plan',
			class: 'Class',
			network: 'Network',
			benefits: 'Benefits',
			features: 'Features',
			policyHolder: 'Policy holder',
			premium: 'Premium',
			subtotal: 'Subtotal',
			total: 'Total',
			members: 'Members',
			membersTotal: 'Insured members',
			membersSubmitted: 'Submitted',
			viewMembers: 'View members',
			coverage: 'Coverage',
			documents: 'Documents',
			payments: 'Payments',
			instalments: 'Instalment plan',
			refunded: 'Refunded',
			benefit: 'Benefit',
			coverageValue: 'Coverage',
			notes: 'Notes / Conditions',
			document: 'Document',
			date: 'Date',
			provider: 'Provider',
			amount: 'Amount',
			paid: 'Paid',
			of: 'of',
			nextDue: 'Next due',
			download: 'Download {name}',
			cancelPolicy: 'Cancel policy',
			cancelTitle: 'Cancel this policy?',
			cancelBody:
				'This cancels policy {policyNumber} for every insured on it and cannot be undone.',
			cancelYes: 'Yes, cancel policy',
			cancelNo: 'No, keep policy',
			cancelled: 'Policy cancelled',
			cancelError: 'Could not cancel this policy',
		},
		newFormQuote: {
			title: 'New Form Quote',
			body: 'You can buy a new form quote by clicking on this button.',
			button: 'Request New Form Quote',
			opensInNewTab: 'Opens in a new tab.',
		},
		profile: {
			title: 'Profile',
			loadError: 'Could not load your profile. Please try again.',
			details: 'Account details',
			fullName: 'Full name',
			email: 'Email',
			emailHint: 'Your sign-in email cannot be changed.',
			accountType: 'Account type',
			companyAdmin: 'Company admin',
			tenantAdmin: 'Sales dashboard admin',
			company: 'Company',
			policies: 'Policies',
			memberSince: 'Member since',
			lastLogin: 'Last sign-in',
			save: 'Save',
			saved: 'Your name has been saved.',
			saveError: 'Could not save your name.',
			changePassword: 'Change password',
			currentPassword: 'Current password',
			newPassword: 'New password',
			confirmPassword: 'Confirm new password',
			passwordsDiffer: 'The new passwords do not match.',
			passwordChanged:
				'Password changed. Other devices have been signed out.',
			passwordTooMany: 'Too many attempts. Please try again later.',
			passwordError: 'Could not change your password.',
		},
	},
} as const

/** Plain recursive merge: `override` wins at every leaf; anything it omits
 * falls through to `base`. Arrays and non-plain-object values from
 * `override` replace `base` outright (no element-wise array merge) — every
 * consumer here treats arrays as atomic values, never as append targets. */
export function mergeDeep<T extends Record<string, unknown>>(
	base: T,
	override: Record<string, unknown> | undefined | null
): T {
	if (!override) return base
	const result: Record<string, unknown> = { ...base }
	for (const key of Object.keys(override)) {
		const baseValue = result[key]
		const overrideValue = override[key]
		if (isPlainObject(baseValue) && isPlainObject(overrideValue)) {
			result[key] = mergeDeep(
				baseValue as Record<string, unknown>,
				overrideValue as Record<string, unknown>
			)
		} else {
			result[key] = overrideValue
		}
	}
	return result as T
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
	return (
		typeof value === 'object' &&
		value !== null &&
		!Array.isArray(value) &&
		value.constructor === Object
	)
}
